import React, { useMemo } from 'react';
import { getHeatmapColor } from '../../utils/chartUtils';
import { HEATMAP_COLORS } from '../../constants/chartConstants';

/**
 * Helper function to extract value from item using various mapping types
 */
function extractValue(item, mapping) {
  if (!mapping) return null;

  // Type 1: String - Direct column name
  if (typeof mapping === 'string') {
    return item[mapping];
  }

  // Type 2: Array - Combine multiple columns
  if (Array.isArray(mapping)) {
    if (mapping.length === 1) {
      return item[mapping[0]];
    }
    
    // Combine multiple columns with separator
    const separator = mapping.separator || ' - ';
    return mapping
      .filter(col => typeof col === 'string')
      .map(col => item[col])
      .filter(val => val !== null && val !== undefined && val !== '')
      .join(separator);
  }

  // Type 3: Object with columns and separator
  if (typeof mapping === 'object' && mapping.columns) {
    const separator = mapping.separator || ' - ';
    return mapping.columns
      .map(col => item[col])
      .filter(val => val !== null && val !== undefined && val !== '')
      .join(separator);
  }

  // Type 4: Function - Custom extraction
  if (typeof mapping === 'function') {
    return mapping(item);
  }

  return null;
}

/**
 * Fully Responsive Heatmap Table Component with Multi-Column Support
 * 
 * @param {Array} data - Data in format: [{source, target, value, userClass?}]
 *                       OR with columnMapping for custom columns
 * @param {Object} columnMapping - Optional column mapping
 *   Examples:
 *     - { source: 'from', target: 'to', value: 'count' }
 *     - { source: ['street', 'direction'], target: ['street', 'direction'], value: 'count' }
 *     - { source: (item) => item.x + item.y, target: 'dest', value: 'count' }
 */
export default function HeatmapTable({
  data = [],
  title,
  subtitle,
  showLegend = true,
  showValues = true,
  compact = false,
  columnMapping = null,  // NEW: Multi-column mapping support
}) {
  // Normalize data using multi-column mapping
  const normalizedData = useMemo(() => {
    if (!columnMapping) {
      return data; // Already in standard format
    }

    return data.map(item => ({
      source: extractValue(item, columnMapping.source),
      target: extractValue(item, columnMapping.target),
      value: extractValue(item, columnMapping.value),
      userClass: columnMapping.userClass 
        ? extractValue(item, columnMapping.userClass)
        : null,
    }));
  }, [data, columnMapping]);

  // Transform normalized data into matrix format
  const { matrix, rowLabels, colLabels, maxValue, stats } = useMemo(() => {
    if (!normalizedData || normalizedData.length === 0) {
      return { matrix: [], rowLabels: [], colLabels: [], maxValue: 0, stats: null };
    }

    // Extract unique sources and targets, sorted
    const sources = [...new Set(normalizedData.map(d => d.source))].sort();
    const targets = [...new Set(normalizedData.map(d => d.target))].sort();

    // Create matrix
    const matrix = sources.map(source => {
      return targets.map(target => {
        const item = normalizedData.find(d => d.source === source && d.target === target);
        return {
          value: item?.value || 0,
          userClass: item?.userClass || null,
          source,
          target,
        };
      });
    });

    // Calculate max value
    const maxValue = Math.max(...normalizedData.map(d => d.value || 0));

    // Calculate statistics
    const totalValue = normalizedData.reduce((sum, d) => sum + (d.value || 0), 0);
    const avgValue = (totalValue / normalizedData.length).toFixed(2);
    const stats = {
      total: totalValue,
      average: avgValue,
      max: maxValue,
      min: Math.min(...normalizedData.map(d => d.value || 0)),
      cells: normalizedData.length,
    };

    return { matrix, rowLabels: sources, colLabels: targets, maxValue, stats };
  }, [normalizedData]);

  if (normalizedData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500 text-sm">No data available</p>
      </div>
    );
  }

  return (
    <div className=''>
      <div className="bg-white rounded-lg overflow-x-auto">
        <div className={compact ? 'p-2' : 'p-4 md:p-6'}>

          {showLegend && !compact && (
            <div className="mb-4">
              <HeatmapLegend maxValue={maxValue} />
            </div>
          )}
          
          {/* Table Container */}
          <div className="overflow-x-auto -mx-2 md:mx-0 px-2 md:px-0">
            <div className="align-middle">
              <div className="overflow-hidden border border-gray-300 rounded-lg">
                <table className="w-full divide-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th
                        scope="col"
                        className="sticky left-0 z-20 bg-gray-100 px-2 md:px-3 py-2 md:py-3 text-left text-xs md:text-sm font-semibold text-gray-700 border-r border-gray-300"
                      >
                        From / To
                      </th>

                      {colLabels.map((label, idx) => (
                        <th
                          key={idx}
                          scope="col"
                          className="px-2 md:px-3 py-2 md:py-3 text-center text-xs md:text-sm font-semibold text-gray-700 border-r border-gray-300 last:border-r-0"
                        >
                          <div className="" title={label}>
                            {label}
                          </div>
                        </th>
                      ))}

                      {!compact && (
                        <th
                          scope="col"
                          className="sticky right-0 z-20 bg-blue-100 px-2 md:px-3 py-2 md:py-3 text-center text-xs md:text-sm font-bold text-blue-800 border-l-2 border-blue-300"
                        >
                          <span className="hidden md:inline">Row Total</span>
                          <span className="md:hidden">Total</span>
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-300 bg-white">
                    {/* Data Rows */}
                    {matrix.map((row, rowIdx) => {
                      const rowTotal = row.reduce((sum, cell) => sum + cell.value, 0);
                      
                      return (
                        <tr key={rowIdx} className="hover:bg-gray-50 transition-colors">
                          {/* Row Label - Sticky */}
                          <td className="sticky left-0 z-10 bg-gray-50 px-2 md:px-3 py-2 md:py-3 text-xs md:text-sm font-medium text-gray-900 border-r border-gray-300 whitespace-nowrap">
                            <div className="truncate max-w-[100px] md:max-w-[160px]" title={rowLabels[rowIdx]}>
                              {rowLabels[rowIdx]}
                            </div>
                          </td>

                          {/* Data Cells */}
                          {row.map((cell, colIdx) => {
                            const bgColor = cell.value > 0
                              ? getHeatmapColor(cell.value, maxValue, false)
                              : '#ffffff';
                            
                            const textColor = cell.value > 0 && cell.value / maxValue > 0.5
                              ? '#ffffff'
                              : '#1f2937';

                            return (
                              <td
                                key={colIdx}
                                className="px-2 md:px-3 py-2 md:py-3 text-center text-xs md:text-sm font-bold border-r border-gray-200 transition-all hover:ring-2 hover:ring-inset hover:ring-blue-500 cursor-pointer last:border-r-0"
                                style={{
                                  backgroundColor: bgColor,
                                  color: textColor,
                                }}
                                title={cell.value > 0 ? `${cell.source} → ${cell.target}: ${cell.value.toLocaleString()}` : 'No traffic'}
                              >
                                {cell.value > 0 ? (
                                  showValues ? cell.value.toLocaleString() : '●'
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            );
                          })}

                          {/* Row Total Cell */}
                          {!compact && (
                            <td
                              className="sticky right-0 z-10 bg-blue-50 px-2 md:px-3 py-2 md:py-3 text-center text-xs md:text-sm font-bold border-l-2 border-blue-300"
                              style={{
                                color: rowTotal > 0 ? getHeatmapColor(rowTotal, maxValue * colLabels.length, false) : '#6b7280'
                              }}
                            >
                              {rowTotal.toLocaleString()}
                            </td>
                          )}
                        </tr>
                      );
                    })}

                    {/* Column Totals Row */}
                    {!compact && (
                      <tr className="bg-green-100 font-bold">
                        {/* Column Total Label */}
                        <td className="sticky left-0 z-10 bg-green-100 px-2 md:px-3 py-2 md:py-3 text-xs md:text-sm font-bold text-green-800 border-r border-green-300 whitespace-nowrap">
                          Column Total
                        </td>

                        {/* Column Total Values */}
                        {colLabels.map((label, colIdx) => {
                          const colTotal = matrix.reduce((sum, row) => sum + row[colIdx].value, 0);
                          
                          return (
                            <td
                              key={colIdx}
                              className="px-2 md:px-3 py-2 md:py-3 text-center text-xs md:text-sm font-bold border-r border-green-200 last:border-r-0"
                              style={{
                                color: colTotal > 0 ? getHeatmapColor(colTotal, maxValue * rowLabels.length, false) : '#6b7280'
                              }}
                            >
                              {colTotal.toLocaleString()}
                            </td>
                          );
                        })}

                        {/* Grand Total */}
                        <td className="sticky right-0 z-10 bg-gray-100 px-2 md:px-3 py-2 md:py-3 text-center text-xs md:text-sm font-bold text-gray-900 border-l-2 border-gray-400">
                          {stats.total.toLocaleString()}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Heatmap Legend Component
 */
export const HeatmapLegend = ({ maxValue }) => {
  const { gradient, thresholds } = HEATMAP_COLORS;

  return (
    <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded border border-gray-200">
      <span className="text-sm font-medium text-gray-700 mr-2">
        Traffic Intensity:
      </span>
      <div className="flex items-center gap-1">
        {gradient.map((color, index) => (
          <div key={index} className="flex flex-col items-center">
            <div
              className="w-12 h-4 rounded"
              style={{ backgroundColor: color }}
            />
            <span className="text-xs text-gray-600 mt-1">
              {index === 0
                ? "Low"
                : index === gradient.length - 1
                  ? "High"
                  : Math.round(thresholds[index] * 100) + "%"}
            </span>
          </div>
        ))}
      </div>
      <span className="text-xs text-gray-500 ml-2">
        (Max: {maxValue?.toLocaleString()})
      </span>
    </div>
  );
};